import { usePageTitle } from "hooks/usePageTitle";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import GmailLogo from "static/images/GmailLogo.svg";
import OutlookLogo from "static/images/OutlookLogo.svg";
import { sendMagicLink } from "../../apis/magicLinkApis";
import {
  GMAIL_LINK,
  INCORRECT_OTP_CODE,
  LINK_EXPIRED_CODE,
  MAGIC_LINK_EXPIRY_MINUTES,
  OTP_SUCCESS,
  OUTLOOK_LINK,
  PAYLOAD,
  TOKEN,
} from "../../constants/magicLink";
import { AuthButtonBar } from "components/Auth/AuthButtonBar";
import { AuthUIWrapper } from "components/Auth/AuthUIWrapper";
import { SigninLayout } from "components/Auth/SigninLayout";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { SmallWarningInfo } from "components/icons/SmallWarningInfo";
import { Button } from "components/DesignSystem/Button/Button";
import { SolidCross } from "components/icons/SolidCross";
import { isV1SignupInvite } from "constants/signupInvite";
import { isCustomerSignupUser } from "utils/isCustomerSignupUser";
import { clearSignupInvite, readSignupInvite } from "utils/signupInviteStorage";
import { readPendingSignupFlags } from "utils/readPendingSignupFlags";
import { openLink } from "../../utils/openLink";
import OtpBox from "../design/otpBox";
import { OtpResendTimer } from "./OtpResendTimerText";

interface MagicLinkPayload {
  email: string;
}

declare const grecaptcha: {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

export const VerifyOtpPage = () => {
  usePageTitle("Email verify");
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const { hash } = useLocation();
  const history = useHistory();
  const { loginMagicUser } = useContext(authContext);
  const [inCorrectOtp, setInCorrectOtp] = useState(false);
  const [payload, setPayload] = useState<MagicLinkPayload | undefined>();
  const [showTimer, setShowTimer] = useState(false);
  const [key, setKey] = useState(0);
  const storedOtp = useRef("");
  const query = useQuery();
  const origin = query.get("origin");
  const [otp, setOtp] = useState("");

  const loginUser = async (updatedHash: string | null) => {
    try {
      const code = await loginMagicUser(PAYLOAD, updatedHash);
      updateData(code);
    } catch (e: any) {
      alertToast({
        message: e?.response?.data?.error?.message,
        position: "bottom-left",
      });
    }
  };

  useEffect(() => {
    if (otp.length < 6) {
      setInCorrectOtp(false);
    }
  }, [otp]);

  useEffect(() => {
    if (hash) {
      const updatedHash = hash.substring(1, hash.length);
      loginUser(updatedHash);
    } else {
      const magicLinkData = localStorage.getItem("magicLinkData");
      const storedPayload: MagicLinkPayload | undefined =
        magicLinkData && magicLinkData !== "undefined"
          ? JSON.parse(magicLinkData)
          : {};
      setPayload(storedPayload);
    }
  }, []);

  const updateData = (code: number | boolean | string) => {
    switch (code) {
      case INCORRECT_OTP_CODE: {
        setInCorrectOtp(true);
        break;
      }
      case LINK_EXPIRED_CODE: {
        history.push("/signin");
        break;
      }
      case true: {
        const invite = readSignupInvite();
        const isV1Signup =
          isV1SignupInvite(invite) ||
          !isCustomerSignupUser(readPendingSignupFlags());

        if (isV1Signup) {
          clearSignupInvite();
          history.push("/phoneverify");
          break;
        }

        history.push("/signup");
        break;
      }
      default:
        return;
    }
  };

  const verifyOtp = async (otp?: string) => {
    const loadingtoastId = loadingToast({
      title: "Verifying",
      position: "bottom-left",
    });
    try {
      let code: number | boolean | string;
      if (otp) {
        storedOtp.current = otp;
        code = await loginMagicUser(TOKEN, otp, payload);
      } else {
        code = await loginMagicUser(TOKEN, storedOtp.current, payload);
      }
      updateData(code);
    } catch (e: any) {
      alertToast({
        message: e?.response?.data?.error?.message,
        position: "bottom-left",
      });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  const redirectToMail = (path: string | null) => {
    if (path) {
      openLink(path);
    }
  };

  const handleOpenGmail = () => redirectToMail(GMAIL_LINK);
  const handleOpenOutlook = () => redirectToMail(OUTLOOK_LINK);

  const resendOtp = () => {
    grecaptcha.ready(function () {
      grecaptcha
        .execute(process.env.PUBLIC_RECAPTCHA_KEY as string, {
          action: "submit",
        })
        .then(async function (token) {
          try {
            await sendMagicLink({
              email: payload?.email as string,
              captcha_value: token,
              accept_tos: true,
            });
            setOtp("");
            setShowTimer(true);
            setKey((prev) => prev + 1);
            successToast({ message: OTP_SUCCESS, position: "bottom-left" });
          } catch (e: any) {
            alertToast({
              message: e?.response?.data?.error?.message,
              position: "bottom-left",
            });
          }
        });
    });
  };

  const originFromSignin = origin === "signin";

  const goBack = () => {
    if (originFromSignin) {
      history.push("/signin");
    } else {
      history.push("/signup?signupform=true");
    }
  };

  if (originFromSignin) {
    return (
      <SigninLayout>
        <div className="t-flex t-flex-col t-gap-10">
          <div>
            <Button
              customType="link"
              size="small"
              className="!t-px-0"
              onClick={goBack}
              leftIcon={<ArrowLeft size="16" />}
            >
              Go back
            </Button>
          </div>
          <div className="t-flex t-flex-col t-gap-8">
            <div className="t-flex t-flex-col t-gap-1.5">
              <div className="t-text-title-h1 t-font-normal t-text-text-100 sm:t-text-title-h2">
                Check your inbox
              </div>
              <div className="t-text-body t-text-text-30 sm:t-text-body-sm-lite">
                A login link has been sent to {payload?.email}. Click it or use
                the OTP to sign in, the link will expire in{" "}
                {MAGIC_LINK_EXPIRY_MINUTES} minutes.
              </div>
            </div>
            <div className="t-flex t-flex-col t-gap-3">
              <div className="t-flex t-flex-col t-gap-1.5">
                <OtpBox
                  otp={otp}
                  setOtp={setOtp}
                  getOtp={verifyOtp}
                  inCorrectOtp={inCorrectOtp}
                  width="fit-content"
                  setInvalidOtp={setInCorrectOtp}
                  className="!t-mx-0"
                />
                {inCorrectOtp && (
                  <div className="t-flex t-items-center t-gap-1 t-text-body-sm-italic t-italic t-text-red">
                    <SmallWarningInfo width="12" height="12" />
                    <span>Invalid OTP. Please try again!</span>
                  </div>
                )}
              </div>
              {showTimer ? (
                <OtpResendTimer
                  setShowTimer={setShowTimer}
                  className="t-text-body-sm t-italic t-text-text-30"
                />
              ) : (
                <div className="t-flex t-flex-wrap t-items-center t-gap-1 t-text-body-sm t-italic t-text-text-30">
                  Can’t find link? Check your spam folder or
                  <Button
                    customType="link"
                    size="xsmall"
                    className="!t-px-0 !t-h-auto t-text-button-sm"
                    onClick={resendOtp}
                  >
                    Resend OTP
                  </Button>
                </div>
              )}
            </div>
            <div className="t-flex t-gap-3">
              <Button
                onClick={handleOpenGmail}
                leftIcon={<img src={GmailLogo} alt="" />}
              >
                Open Gmail
              </Button>
              <Button
                onClick={handleOpenOutlook}
                leftIcon={<img src={OutlookLogo} alt="" />}
              >
                Open Outlook
              </Button>
            </div>
          </div>
        </div>
      </SigninLayout>
    );
  }

  return (
    <AuthUIWrapper buttonBar={<AuthButtonBar nextDisabled onBack={goBack} />}>
      <div className="t-mt-[80px] sm:t-mt-8">
        <div className="t-text-subtext-sm t-text-neutral-40 t-mb-4 t-mt-[72px] sm:t-mt-8">
          Step 1 of 4: Email verification
        </div>
        <div className="t-text-subtitle-lg t-text-text-100 t-mb-2">
          Verify your email address
        </div>
        <div className="t-text-body-sm t-text-text-30 t-mb-8">
          A login link has been sent to {payload?.email}. Click it or use the
          OTP to sign in, the link will expire in {MAGIC_LINK_EXPIRY_MINUTES}{" "}
          minutes.
        </div>
        <OtpBox
          otp={otp}
          setOtp={setOtp}
          getOtp={verifyOtp}
          inCorrectOtp={inCorrectOtp}
          width="100%"
          setInvalidOtp={setInCorrectOtp}
          className="t-mb-3 !t-mx-0"
        />
        {inCorrectOtp && (
          <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-red t-font-light t-mb-3">
            <SolidCross />
            <span>Invalid OTP. Please try again!</span>
          </div>
        )}

        <div className="t-mb-6">
          {showTimer ? (
            <OtpResendTimer setShowTimer={setShowTimer} />
          ) : (
            <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1 t-italic">
              Can’t find link? Check your spam folder or
              <Button customType="link" size="xsmall" onClick={resendOtp}>
                resend
              </Button>
            </div>
          )}
        </div>
        <div className="t-flex t-gap-3">
          <Button onClick={handleOpenGmail} size="small">
            <div className="t-flex t-gap-1 t-items-center">
              <img src={GmailLogo} alt="" />
              <span>Open Gmail</span>
            </div>
          </Button>
          <Button onClick={handleOpenOutlook} size="small">
            <div className="t-flex t-gap-1 t-items-center">
              <img src={OutlookLogo} alt="" />
              <span>Open Outlook</span>
            </div>
          </Button>
        </div>
      </div>
    </AuthUIWrapper>
  );
};
